import * as R from '../../Router/routes';
import {
  AsyncCardPayment,
  AsyncExtensions,
  AsyncFullRepayment,
} from '../../Router/Router';
import { LatestLoan } from '../../hooks/webapi/useLatestLoan';
import useClientDebtRestructuring from '../../hooks/webapi/useClientDebtRestructuring';
import useError from '../../hooks/useError';

import { ReactComponent as BankCardIcon } from '../../assets/icons/bank-card-circle.svg';
import { ReactComponent as BizumIcon } from '../../assets/icons/bizum.svg';
import {
  Card,
  MenuButton,
  PenaltyNotification,
  Placeholder,
} from '../../components';
import { dateFormat, numberToCurrency } from '../../utils/translate';
import { getBackUrlMenuScreens } from '../../utils/getUrl';
import { getDecimal, getInt } from '../ActiveLoan/utils';
import { getOverdueType } from '../../utils/overdue';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as InfoIcon } from '../../assets/icons/error-info.svg';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import {
  QUERY_BACK_ROUTE,
  QUERY_BIZUM,
  QUERY_PARTIAL_AMOUNT,
} from '../../utils/constants';
import { useActiveLoan } from '../ActiveLoan/useActiveLoan';
import {
  useCardPayments,
  useClient,
  useLatestLoan,
  usePaymentSchedule,
} from '../../hooks/webapi';
import { useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import dayjs from 'dayjs';
import styles from './RepaymentOverdue6Or8.module.scss';

function preloadExtensions() {
  AsyncExtensions.preload();
}

function preloadCardPayment() {
  AsyncCardPayment.preload();
}

function preloadFullRepayment() {
  AsyncFullRepayment.preload();
}

export default memo(function Repayment() {
  const { t } = useTranslation('repayment');
  const { isOverdue, isBrokenAgreement } = useActiveLoan();
  const { showError } = useError();
  const { client } = useClient();
  const { fetchPaymentSchedule } = usePaymentSchedule();
  const { latestLoan, fetchLatestLoan } = useLatestLoan();
  const [loading, setLoading] = useState(true);
  const [navigationDisabled, setNavigationDisabled] = useState(false);
  const navigate = useNavigate();
  const { getBizumPayments } = useCardPayments();
  const { isDebtRestructuring } = useClientDebtRestructuring();

  const urlParams = new URLSearchParams(
    decodeURIComponent(window.location.search),
  );
  const partialAmount = urlParams.get('partialAmount');
  const isPartialPayment = Boolean(partialAmount);

  const latestLoanRef = useRef<LatestLoan | null | undefined>(latestLoan);

  const isCons7 =
    latestLoan?.productCode === 'CONS' && latestLoan?.productNumber === '7';

  const amountFromBE = latestLoanRef.current?.payNowAmount;

  const amountToPay = isPartialPayment ? Number(partialAmount) : amountFromBE;

  useEffect(() => {
    preloadExtensions();
    preloadCardPayment();
    preloadFullRepayment();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const loan = await fetchLatestLoan();

        if (!loan) {
          navigate(R.REPEATED_LOAN, { replace: true });

          return;
        }

        latestLoanRef.current = loan;

        if (loan.debtCollectLoanAgreement && !isPartialPayment) {
          navigate(R.ACTIVE_LOAN, { replace: true });

          return;
        }

        await fetchPaymentSchedule();

        setLoading(false);
      } catch {}
    })();
  }, [fetchPaymentSchedule, fetchLatestLoan, navigate]);

  /** The extension with the highest term */
  const maximumExtension = useMemo(
    () =>
      latestLoan?.loanExtensionFees.reduce(
        (acc: any, item) => (acc = acc.term > item.term ? acc : item),
        0,
      ),
    [latestLoan?.loanExtensionFees],
  );

  const overdueType = getOverdueType(latestLoan);

  function renderPaymentInfo() {
    return (
      <Card className={styles.payment} dataTest="repaymentOverdue-paymentInfo">
        <div className={styles['pending-debt-card']}>
          <Placeholder
            loading={loading}
            className={styles.bold}
            dataTest="repaymentOverdue-paymentInfo-title"
          >
            {isPartialPayment ? t('pay_now_partial') : t('pay_now')}
          </Placeholder>
          <Placeholder
            loading={loading}
            className={classes(
              styles['pending-debt-card-amount'],
              styles[`pending-debt-card-amount-${overdueType}`],
            )}
            dataTest="repaymentOverdue-paymentInfo-debtAmount"
          >
            <span
              className={styles['amount-decimals']}
              data-test="repaymentOverdue-paymentInfo-debtAmount-int"
            >
              {getInt(amountToPay)}
            </span>
            <span
              className={classes(
                styles['pending-debt-card-decimals'],
                styles[`pending-debt-card-amount-${overdueType}`],
              )}
              data-test="repaymentOverdue-paymentInfo-debtAmount-decimals"
            >
              {getDecimal(amountToPay) || ',00'} {t('common:currency')}
            </span>
          </Placeholder>
        </div>
        {Boolean(latestLoan?.earlyFullRepaymentFee) && !isCons7 && (
          <>
            <div data-test="early-repayment-fee-data">
              <div className={styles.row}>
                <Placeholder
                  loading={loading}
                  data-test="early-repayment-fee-data-openAmount-text"
                >
                  {t('open_amount')}
                </Placeholder>
                <Placeholder
                  loading={loading}
                  data-test="early-repayment-fee-data-openAmount-value"
                >
                  {numberToCurrency(latestLoan?.openAmount)}
                </Placeholder>
              </div>
              <div className={styles.row}>
                <Placeholder
                  loading={loading}
                  dataTest="early-repayment-fee-data-full-text"
                >
                  {t('early_repayment_fee')}
                </Placeholder>
                <Placeholder
                  loading={loading}
                  dataTest="early-repayment-fee-data-full-value"
                >
                  {numberToCurrency(latestLoan?.earlyFullRepaymentFee)}
                </Placeholder>
              </div>
            </div>
            <div className={styles['fee-notification']}>
              <div className={styles['fee-icon']}>
                <InfoIcon data-test="early-repayment-fee-data-notif-icon" />
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('early_repayment_text'),
                }}
                data-test="early-repayment-fee-data-notif-text"
              ></div>
            </div>
          </>
        )}
      </Card>
    );
  }

  const showRepaymentButtons =
    latestLoan?.productCode !== 'CONS' ||
    latestLoan?.nearestPaymentDate <= dayjs().format('YYYY-MM-DD');

  const showPenaltyNotification =
    latestLoan &&
    (latestLoan.penaltyAmount > 0 || latestLoan.commissionAmount > 0);

  const search = `${QUERY_BACK_ROUTE}=${R.REPAYMENT}${
    isPartialPayment ? `&${QUERY_PARTIAL_AMOUNT}=${partialAmount}` : ''
  }`;

  const onBizumOptionClicked = async () => {
    const successURLPayment =
      latestLoanRef.current?.productCode === 'CONS' || isDebtRestructuring
        ? process.env.REACT_APP_ROOT_URL + R.CARD_PAYMENT_CONS_SUCCESS
        : process.env.REACT_APP_ROOT_URL + R.CARD_PAYMENT_MINI_SUCCESS;

    const failureURLPayment =
      latestLoanRef.current?.productCode === 'CONS' || isDebtRestructuring
        ? process.env.REACT_APP_ROOT_URL +
          R.CARD_PAYMENT_CONS_FAILURE +
          `?${QUERY_BIZUM}&${search}`
        : process.env.REACT_APP_ROOT_URL +
          R.CARD_PAYMENT_FAILURE +
          `?${QUERY_BIZUM}&${search}`;

    if (!amountToPay) {
      showError({ message: t('error:invalidLoan') });
    } else {
      setNavigationDisabled(true);
      setLoading(true);

      const response = await getBizumPayments({
        body: {
          amount: amountToPay,
          successUrl: successURLPayment,
          failureUrl: failureURLPayment,
        },
      });

      setNavigationDisabled(false);
      setLoading(false);

      if (response?.payFormUrl) {
        navigate(response?.payFormUrl);
      } else if (response?.status === 'ERROR') {
        if (response?.errorCode === 'DUPLICATE_PAYMENT' && false) {
          showError({ message: t('error:duplicatePayment') });
        } else showError();
      }
    }
  };

  const getPauseInfo = () => {
    const isMini8 =
      latestLoan?.productCode === 'MINI' && latestLoan?.productNumber === '8';

    if (latestLoan?.overDueDays && latestLoan.overDueDays > 5) {
      if (isMini8) return t('postpone_info_mini8');

      if (latestLoan?.productNumber === '6') return t('postpone_info_6');
    }

    return t('postpone_info');
  };

  const onCardOptionClicked = () => {
    const cardPaymentRoute = `${R.CARD_PAYMENT}?${search}`;
    if (isPartialPayment) {
      navigate(`${cardPaymentRoute}&${search}`);
      return;
    }
    navigate(cardPaymentRoute);
  };

  const backUrl = isPartialPayment
    ? `${R.REPAYMENT_BROKEN_LOAN}?${search}`
    : getBackUrlMenuScreens(client, latestLoan);

  return (
    <Dashboard
      backUrl={backUrl}
      backUrlDisabled={navigationDisabled}
      menuDisabled={navigationDisabled}
      small
      className={styles['body-wrapper']}
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <div className={styles.heading}>
        <Placeholder loading={loading} dataTest="repaymentOverdue-2-heading">
          {isBrokenAgreement ? t('heading_broken_loan') : t('heading')}
        </Placeholder>
      </div>

      {showPenaltyNotification && latestLoan && !isOverdue && (
        <PenaltyNotification
          loading={loading}
          penalty={latestLoan.penaltyAmount}
          overdue={latestLoan.overDueDays}
          commission={latestLoan.commissionAmount}
        />
      )}

      {!showRepaymentButtons && !showPenaltyNotification && (
        <div
          data-test="payment-notification"
          className={styles['payment-notification']}
        >
          <div className={styles['info-icon']}>
            <InfoIcon data-test="payment-notification-icon" />
          </div>
          <Placeholder loading={loading} dataTest="payment-notification-text">
            <span
              dangerouslySetInnerHTML={{
                __html: t('payment_notification', {
                  date: dateFormat(latestLoan?.nearestPaymentDate),
                }),
              }}
            />
          </Placeholder>
        </div>
      )}

      {renderPaymentInfo()}

      {showRepaymentButtons && (
        <Card dataTest="repayment-buttons" className={styles.menu}>
          <MenuButton
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Card payment button"
            loading={loading}
            dataTest="card-payment-button"
            className={styles['menu-button']}
            heading={t('card_heading')}
            Icon={BankCardIcon}
            onClick={onCardOptionClicked}
          />
          <MenuButton
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Bizum payment button"
            loading={loading}
            dataTest="bizum-payment-button"
            className={styles['menu-button']}
            heading={t('bizum_heading_overview')}
            Icon={BizumIcon}
            onClick={onBizumOptionClicked}
          />
          {Boolean(maximumExtension) && !isBrokenAgreement && (
            <MenuButton
              analyticsIsTrackable={true}
              analyticsTrackingDescription="Postpone payment"
              loading={loading}
              dataTest="extensions-button"
              className={styles['menu-button']}
              heading={t('postpone_heading')}
              info={getPauseInfo()}
              Icon={PauseIcon}
              url={`${R.EXTENSIONS}?PID=${latestLoan?.productNumber}`}
            />
          )}
        </Card>
      )}
    </Dashboard>
  );
});
